import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { calculateCvssScore, CvssMetrics, generateCvssVector } from './cvss.util';

@Component({
  selector: 'dhats-cvss-calculator',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-bold uppercase tracking-widest text-zinc-400">CVSS v3.1 Calculator</h3>
        <div class="flex items-center gap-2">
          <span class="text-2xl font-black" [style.color]="getScoreColor(score())">{{ score() | number:'1.1-1' }}</span>
          <span class="text-xs uppercase tracking-widest px-2 py-1 rounded"
                [style.background-color]="getSeverityBadgeStyle(score()).bg"
                [style.color]="getSeverityBadgeStyle(score()).color"
                [style.border]="'1px solid ' + getSeverityBadgeStyle(score()).border">{{ getSeverity(score()) }}</span>
        </div>
      </div>
      <p class="text-xs font-mono text-zinc-500 break-all bg-black/20 p-2 rounded">{{ vector() || 'CVSS:3.1/...' }}</p>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <!-- Attack Vector -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Attack Vector (AV)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of avOptions" type="button"
                    (click)="setMetric('AV', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.AV === opt.value"
                    [class.text-white]="metrics.AV === opt.value"
                    [class.bg-white_10]="metrics.AV !== opt.value"
                    [class.text-zinc-400]="metrics.AV !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- Attack Complexity -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Attack Complexity (AC)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of acOptions" type="button"
                    (click)="setMetric('AC', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.AC === opt.value"
                    [class.text-white]="metrics.AC === opt.value"
                    [class.bg-white_10]="metrics.AC !== opt.value"
                    [class.text-zinc-400]="metrics.AC !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- Privileges Required -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Privileges Req (PR)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of prOptions" type="button"
                    (click)="setMetric('PR', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.PR === opt.value"
                    [class.text-white]="metrics.PR === opt.value"
                    [class.bg-white_10]="metrics.PR !== opt.value"
                    [class.text-zinc-400]="metrics.PR !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- User Interaction -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">User Interaction (UI)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of uiOptions" type="button"
                    (click)="setMetric('UI', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.UI === opt.value"
                    [class.text-white]="metrics.UI === opt.value"
                    [class.bg-white_10]="metrics.UI !== opt.value"
                    [class.text-zinc-400]="metrics.UI !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- Scope -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Scope (S)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of sOptions" type="button"
                    (click)="setMetric('S', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.S === opt.value"
                    [class.text-white]="metrics.S === opt.value"
                    [class.bg-white_10]="metrics.S !== opt.value"
                    [class.text-zinc-400]="metrics.S !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- Confidentiality -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Confidentiality (C)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of cOptions" type="button"
                    (click)="setMetric('C', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.C === opt.value"
                    [class.text-white]="metrics.C === opt.value"
                    [class.bg-white_10]="metrics.C !== opt.value"
                    [class.text-zinc-400]="metrics.C !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- Integrity -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Integrity (I)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of iOptions" type="button"
                    (click)="setMetric('I', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.I === opt.value"
                    [class.text-white]="metrics.I === opt.value"
                    [class.bg-white_10]="metrics.I !== opt.value"
                    [class.text-zinc-400]="metrics.I !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
        <!-- Availability -->
        <div class="space-y-2">
          <p class="text-[10px] font-black uppercase tracking-widest text-zinc-500">Availability (A)</p>
          <div class="flex flex-col gap-1">
            <button *ngFor="let opt of aOptions" type="button"
                    (click)="setMetric('A', opt.value)"
                    class="px-2 py-1.5 text-xs rounded text-left transition-colors"
                    [class.bg-brand-500]="metrics.A === opt.value"
                    [class.text-white]="metrics.A === opt.value"
                    [class.bg-white_10]="metrics.A !== opt.value"
                    [class.text-zinc-400]="metrics.A !== opt.value"
                    style="border: 1px solid var(--glass-border)">
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .bg-white_10 { background-color: rgba(255, 255, 255, 0.03); }
    .bg-brand-500 { background-color: rgb(11, 3, 252); }
  `]
})
export class CvssCalculatorComponent implements OnInit, OnChanges {
  @Input() initialMetrics: CvssMetrics | null = null;
  @Output() metricsChange = new EventEmitter<CvssMetrics>();
  @Output() scoreChange = new EventEmitter<number>();

  metrics: CvssMetrics = {};

  avOptions = [
    { label: 'Network (N)', value: 'N' },
    { label: 'Adjacent (A)', value: 'A' },
    { label: 'Local (L)', value: 'L' },
    { label: 'Physical (P)', value: 'P' },
  ];
  acOptions = [
    { label: 'Low (L)', value: 'L' },
    { label: 'High (H)', value: 'H' },
  ];
  prOptions = [
    { label: 'None (N)', value: 'N' },
    { label: 'Low (L)', value: 'L' },
    { label: 'High (H)', value: 'H' },
  ];
  uiOptions = [
    { label: 'None (N)', value: 'N' },
    { label: 'Required (R)', value: 'R' },
  ];
  sOptions = [
    { label: 'Unchanged (U)', value: 'U' },
    { label: 'Changed (C)', value: 'C' },
  ];
  cOptions = [
    { label: 'None (N)', value: 'N' },
    { label: 'Low (L)', value: 'L' },
    { label: 'High (H)', value: 'H' },
  ];
  iOptions = [
    { label: 'None (N)', value: 'N' },
    { label: 'Low (L)', value: 'L' },
    { label: 'High (H)', value: 'H' },
  ];
  aOptions = [
    { label: 'None (N)', value: 'N' },
    { label: 'Low (L)', value: 'L' },
    { label: 'High (H)', value: 'H' },
  ];

  ngOnInit() {
    // Initialization is now handled by ngOnChanges
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialMetrics']) {
      if (this.initialMetrics) {
        this.metrics = { ...this.initialMetrics };
      } else {
        this.metrics = {
          AV: 'N', AC: 'L', PR: 'N', UI: 'N', S: 'U', C: 'H', I: 'H', A: 'H'
        };
      }
      setTimeout(() => this.recalculate(), 0);
    }
  }

  setMetric(key: keyof CvssMetrics, value: any) {
    this.metrics[key] = value;
    this.recalculate();
  }

  recalculate() {
    this.metricsChange.emit(this.metrics);
    const s = calculateCvssScore(this.metrics);
    if (s !== null) {
      this.scoreChange.emit(s);
    }
  }

  score(): number {
    return calculateCvssScore(this.metrics) || 0;
  }

  vector(): string {
    return generateCvssVector(this.metrics);
  }

  getSeverity(s: number): string {
    if (s === 0) return 'None';
    if (s < 4.0) return 'Low';
    if (s < 7.0) return 'Medium';
    if (s < 9.0) return 'High';
    return 'Critical';
  }

  getScoreColor(s: number): string {
    if (s === 0) return 'var(--text-muted)';
    if (s < 4.0) return '#EAB308'; // Low
    if (s < 7.0) return '#F97316'; // Medium
    if (s < 9.0) return '#EF4444'; // High
    return '#7B1F2E'; // Critical
  }

  getSeverityBadgeStyle(s: number): { bg: string; color: string; border: string } {
    if (s === 0) return { bg: 'rgba(161,161,170,0.1)', color: 'var(--text-muted)', border: 'rgba(161,161,170,0.2)' };
    if (s < 4.0) return { bg: 'rgba(234,179,8,0.12)', color: '#EAB308', border: 'rgba(234,179,8,0.2)' };
    if (s < 7.0) return { bg: 'rgba(249,115,22,0.12)', color: '#F97316', border: 'rgba(249,115,22,0.2)' };
    if (s < 9.0) return { bg: 'rgba(239,68,68,0.12)', color: '#EF4444', border: 'rgba(239,68,68,0.2)' };
    return { bg: 'rgba(123,31,46,0.12)', color: '#7B1F2E', border: 'rgba(123,31,46,0.2)' };
  }
}
